import type { AvailableChannels, IWebSocketConnection, SendMessageTypes } from './types'

const DEFAULT_PING_PONG_INTERVAL = 20000
const DEFAULT_RECONNECT_INTERVAL = 1000
const DEFAULT_CONNECT_TIMEOUT = 10000
const DEFAULT_AUTH_TIMEOUT = 10000
const DEFAULT_MAX_RECONNECT_ATTEMPTS = 10
const MAX_RECONNECT_INTERVAL = 60000

type AuthOptions = {
  requiredAuth?: boolean
  token?: string
}

type ChannelSubscription = {
  channel: AvailableChannels
  options?: Record<string, unknown>
  /**
   * Для каналов, где подписка идет списком инструментов (например `bbo`),
   * symbols накапливаются отдельно от options: subscribe и unsubscribe
   * приходят пачками разного состава, и после
   * subscribe([A, B]) + unsubscribe([B]) подписка на A должна остаться.
   */
  symbols?: Set<string>
  refs: number
  symbolRefs?: Map<string, number>
}

type UnsubscribeDecision = {
  shouldSend: boolean
  symbols?: string[]
}

export class WebSocketConnection implements IWebSocketConnection {
  private ws: WebSocket | null = null
  private channelHandlers = new Map<string, Set<(data: unknown) => void>>()
  private subscriptions = new Map<string, ChannelSubscription>()
  private _isConnected = false
  private _isAuthenticated = false
  private intervalPingPong: ReturnType<typeof setInterval> | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private reconnectAttempts = 0
  private manualClose = false

  constructor(
    private url: string,
    private options?: {
      reconnectInterval?: number
      pingPongInterval?: number
      connectTimeout?: number
      authTimeout?: number
      maxReconnectAttempts?: number
    },
    private authOptions?: AuthOptions
  ) {}

  connect(): Promise<void> {
    if (typeof window === 'undefined') {
      return Promise.reject(new Error('WebSocket is not available during SSR'))
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return Promise.resolve()
    }

    this.manualClose = false

    return new Promise((resolve, reject) => {
      let settled = false
      let socket: WebSocket

      try {
        socket = new WebSocket(this.url)
      } catch (error) {
        reject(error)
        return
      }

      this.ws = socket

      const timeout = setTimeout(() => {
        if (settled) return

        settled = true
        socket.close()
        reject(
          new Error(
            `WS connection timed out after ${this.options?.connectTimeout ?? DEFAULT_CONNECT_TIMEOUT}ms`
          )
        )
      }, this.options?.connectTimeout ?? DEFAULT_CONNECT_TIMEOUT)

      socket.onopen = () => {
        if (settled) return

        settled = true
        clearTimeout(timeout)
        this._isConnected = true
        this.initPingPong()
        resolve()
      }

      socket.onmessage = (event: MessageEvent<string>) => {
        this.handleMessage(event.data)
      }

      socket.onclose = () => {
        clearTimeout(timeout)
        this._isConnected = false
        this._isAuthenticated = false
        this.stopPingPong()

        if (this.ws === socket) {
          this.ws = null
        }

        if (!this.manualClose) {
          this.reconnect()
        }
      }

      socket.onerror = (err) => {
        this._isConnected = false
        this.stopPingPong()

        if (!settled) {
          settled = true
          clearTimeout(timeout)
          reject(err)
        }
      }
    })
  }

  private reconnect() {
    if (this.manualClose || this.reconnectTimer) return

    const maxAttempts = this.options?.maxReconnectAttempts ?? DEFAULT_MAX_RECONNECT_ATTEMPTS
    if (this.reconnectAttempts >= maxAttempts) {
      console.error(`WS reconnect failed after ${maxAttempts} attempts`)
      return
    }

    this.reconnectAttempts += 1
    const baseDelay = this.options?.reconnectInterval ?? DEFAULT_RECONNECT_INTERVAL
    const delay = Math.min(
      baseDelay * 2 ** (this.reconnectAttempts - 1),
      MAX_RECONNECT_INTERVAL
    )

    this.reconnectTimer = setTimeout(async () => {
      this.reconnectTimer = null

      try {
        await this.connect()

        if (this.authOptions?.requiredAuth) {
          await this.auth()
        }

        this.reconnectAttempts = 0
        this.subscriptions.forEach((subscription) => {
          this.sendMessage({
            action: 'subscribe',
            channel: subscription.channel,
            ...this.buildSubscriptionOptions(subscription),
          })
        })
      } catch (error) {
        console.error('WS reconnect failed:', error)

        // An auth timeout/error can leave the TCP/WebSocket session open. Close
        // it so the next attempt starts with a clean connection.
        this.ws?.close()

        // connect() may fail before onclose fires (for example in a mock or
        // browser implementation), so ensure the next attempt is scheduled.
        this.reconnect()
      }
    }, delay)
  }

  auth(): Promise<void> {
    if (!this.authOptions?.token) {
      return Promise.reject(new Error('Auth token is missing'))
    }

    return new Promise((resolve, reject) => {
      let settled = false
      let authTimeout: ReturnType<typeof setTimeout>
      const unsubscribe = this.addChannelHandler('__auth__', (message: any) => {
        if (message.type === 'authenticated') {
          settled = true
          this._isAuthenticated = true
          clearTimeout(authTimeout)
          unsubscribe()
          resolve()
        }

        if (message.type === 'error') {
          settled = true
          clearTimeout(authTimeout)
          unsubscribe()
          reject(new Error(message.error ?? 'WS auth failed'))
        }
      })

      authTimeout = setTimeout(() => {
        if (settled) return

        settled = true
        unsubscribe()
        this._isAuthenticated = false
        this.ws?.close()
        reject(
          new Error(
            `WS auth timed out after ${this.options?.authTimeout ?? DEFAULT_AUTH_TIMEOUT}ms`
          )
        )
      }, this.options?.authTimeout ?? DEFAULT_AUTH_TIMEOUT)

      this.sendMessage({
        action: 'auth',
        token: this.authOptions?.token,
      })
    })
  }

  private handleMessage(raw: string) {
    const message = JSON.parse(raw)

    if (message.type === 'authenticated') {
      this.runHandlers('__auth__', message)
      return
    }

    if (message.type === 'error') {
      this.runHandlers('__auth__', message)
      console.error('WS error:', message.error)
      return
    }

    if (message.channel) {
      this.runHandlers(message.channel, message)
    }
  }

  private runHandlers(channel: string, message: unknown) {
    const handlers = this.channelHandlers.get(channel)
    if (!handlers) return

    handlers.forEach((handler) => handler(message))
  }

  private initPingPong() {
    this.stopPingPong()

    this.intervalPingPong = setInterval(() => {
      this.sendMessage({
        action: 'ping',
        timestamp: Date.now(),
      })
    }, this.options?.pingPongInterval ?? DEFAULT_PING_PONG_INTERVAL)
  }

  private stopPingPong() {
    if (!this.intervalPingPong) return

    clearInterval(this.intervalPingPong)
    this.intervalPingPong = null
  }

  sendMessage(payload: SendMessageTypes): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn(`WS not open, cannot send ${payload.action}`)
      return
    }

    this.ws.send(JSON.stringify(payload))
  }

  subscribeToChannelMessages(channel: AvailableChannels, options?: Record<string, unknown>) {
    this.registerSubscription(channel, options)

    this.sendMessage({
      action: 'subscribe',
      channel,
      ...options,
    })

    return () => this.unsubscribeToChannelMessages(channel, options)
  }

  unsubscribeToChannelMessages(channel: AvailableChannels, options?: Record<string, unknown>) {
    const decision = this.unregisterSubscription(channel, options)

    if (!decision.shouldSend) {
      return
    }

    this.sendMessage({
      action: 'unsubscribe',
      channel,
      ...options,
      ...(decision.symbols ? { symbols: decision.symbols } : {}),
    })
  }

  /**
   * Ключ подписки — канал + options без symbols.
   * Так подписки одного канала на разные инструменты не перетирают друг друга
   * (`orderbook` + `{symbol}`), а список symbols одного канала (`bbo`)
   * копится в одной записи.
   */
  private buildSubscriptionKey(channel: AvailableChannels, options?: Record<string, unknown>) {
    const rest: Record<string, unknown> = { ...options }
    delete rest.symbols

    const serializedOptions = Object.keys(rest)
      .sort()
      .map((key) => `${key}=${JSON.stringify(rest[key])}`)
      .join('&')

    return `${channel}|${serializedOptions}`
  }

  private extractSymbols(options?: Record<string, unknown>): string[] | null {
    if (!Array.isArray(options?.symbols)) {
      return null
    }

    return options.symbols.map((symbol) => String(symbol))
  }

  private registerSubscription(channel: AvailableChannels, options?: Record<string, unknown>) {
    const key = this.buildSubscriptionKey(channel, options)
    const symbols = this.extractSymbols(options)
    const subscription = this.subscriptions.get(key)

    if (subscription) {
      subscription.refs += 1

      if (symbols) {
        subscription.symbols ??= new Set()
        subscription.symbolRefs ??= new Map()

        symbols.forEach((symbol) => {
          subscription.symbols?.add(symbol)
          subscription.symbolRefs?.set(symbol, (subscription.symbolRefs.get(symbol) ?? 0) + 1)
        })
      }

      return
    }

    this.subscriptions.set(key, {
      channel,
      options,
      symbols: symbols ? new Set(symbols) : undefined,
      symbolRefs: symbols ? new Map(symbols.map((symbol) => [symbol, 1])) : undefined,
      refs: 1,
    })
  }

  private unregisterSubscription(
    channel: AvailableChannels,
    options?: Record<string, unknown>
  ): UnsubscribeDecision {
    const key = this.buildSubscriptionKey(channel, options)
    const subscription = this.subscriptions.get(key)

    if (!subscription) {
      return { shouldSend: false }
    }

    subscription.refs = Math.max(0, subscription.refs - 1)

    const symbols = this.extractSymbols(options)

    // отписка от части инструментов не должна снимать подписку на весь канал
    if (subscription.symbols && symbols) {
      const releasedSymbols = symbols.filter((symbol) => {
        const refs = subscription.symbolRefs?.get(symbol) ?? 0
        const nextRefs = Math.max(0, refs - 1)

        if (nextRefs) {
          subscription.symbolRefs?.set(symbol, nextRefs)
          return false
        }

        subscription.symbolRefs?.delete(symbol)
        subscription.symbols?.delete(symbol)
        return true
      })

      if (!subscription.symbols.size && !subscription.refs) {
        this.subscriptions.delete(key)
      }

      return releasedSymbols.length
        ? { shouldSend: true, symbols: releasedSymbols }
        : { shouldSend: false }
    }

    if (subscription.refs) {
      return { shouldSend: false }
    }

    this.subscriptions.delete(key)

    return { shouldSend: true }
  }

  /**
   * options на момент подписки могут содержать уже неактуальный symbols,
   * поэтому при переподписке подставляем накопленный список.
   */
  private buildSubscriptionOptions(subscription: ChannelSubscription) {
    const options: Record<string, unknown> = { ...subscription.options }

    if (subscription.symbols) {
      options.symbols = [...subscription.symbols]
    }

    return options
  }

  addChannelHandler<T>(channel: string, handler: (data: T) => void) {
    if (!this.channelHandlers.has(channel)) {
      this.channelHandlers.set(channel, new Set())
    }

    const handlers = this.channelHandlers.get(channel)!
    handlers.add(handler as (data: unknown) => void)

    return () => {
      handlers.delete(handler as (data: unknown) => void)
    }
  }

  close(): void {
    this.manualClose = true
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.reconnectAttempts = 0
    this.stopPingPong()
    this.ws?.close()
    this.ws = null
  }

  get isConnected(): boolean {
    return this._isConnected
  }

  get isAuthenticated(): boolean {
    return this._isAuthenticated
  }
}
